import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { type MfaRequest, type MfaResult } from '@lib/entities/Mfa';

/**
 * Redux twin of the MFA controller slice (lib/state/Mfa.ts, JSAD-7433) — single inflight slot
 * for the portable MFA challenge. In-memory only; not persisted; NOT reset on `auth/loggedOut`
 * (the flag mirrors are PostHog state, and the inflight slot is resolved as `expired` by the
 * logout saga before it dispatches `loggedOut`).
 *
 * The Promise the Zustand slice kept in state lives outside the store
 * (`lib/redux/mfa/inflightRegistry.ts`, keyed by `inflight.id`); `challengeMfa()` dispatches
 * `challengeStarted` synchronously so `isMfaInflight()` → `beginMfa` → challenge in one tick
 * stays atomic (JSAD-7388). Telemetry (`MFA Challenge Started` / `Resolved`, JSAD-6706) is
 * emitted by `mfaSaga`.
 */
export interface MfaInflight {
    /** Key into the inflight registry that owns the Promise. */
    id: string;
    request: MfaRequest;
}

/**
 * Carried on `inflightResolved` so `mfaSaga` can emit `MFA Challenge Resolved` without a store
 * read (the slot is already cleared by the time the saga runs). Optional: a bare
 * `inflightResolved()` still clears the slot and emits nothing.
 */
export interface InflightResolvedPayload {
    request: MfaRequest;
    result: MfaResult;
}

export interface MfaState {
    /** MfaChallengeHost subscribes to mount/unmount the modal. */
    inflight?: MfaInflight;
    /**
     * Mirror of the `mfa-device-verification` master flag, written by MfaChallengeHost.
     *
     * Lets non-React internal call sites (sagas via `select`, the non-hook getters in
     * useMfaChallenge) make MFA-aware decisions — e.g. routing wallet fetches to the v5
     * endpoint — without threading the flag through every action signature. The flag
     * itself only lives in PostHog (hook-only); this is the single in-store mirror.
     */
    isMfaEnabled: boolean;
    /**
     * Mirror of the `mfa-account-verification` flag, written by MfaChallengeHost. Independent
     * of `isMfaEnabled` (that mirrors `mfa-device-verification`). Lets non-React call sites
     * (checkout/cart sagas, the CheckoutService quote/process-payment endpoints) route to the
     * v6 endpoints without threading the flag through every action + service signature.
     */
    isAccVerificationEnabled: boolean;
    /** Post-skip "Setting things up..." view (see `isFinalizingSkip` in lib/state/Mfa.ts). */
    isFinalizingSkip: boolean;
}

const initialState: MfaState = {
    inflight: undefined,
    isMfaEnabled: false,
    isAccVerificationEnabled: false,
    isFinalizingSkip: false
};

const mfaSlice = createSlice({
    name: 'mfa',
    initialState,
    reducers: {
        /** `Mfa.ts:129` `challenge.set` — claims the single inflight slot. */
        challengeStarted: (state, action: PayloadAction<MfaInflight>) => {
            state.inflight = action.payload;
        },
        /**
         * `Mfa.ts:171` `resolveInflight.clear`. The registry resolves the Promise BEFORE this
         * is dispatched so a follow-up `challenge()` from the resolution handler sees an empty
         * slot.
         */
        inflightResolved: (state, _action: PayloadAction<InflightResolvedPayload | undefined>) => {
            state.inflight = undefined;
        },
        /**
         * Mirror the `mfa-device-verification` master flag into the store (called by
         * MfaChallengeHost). No-op when unchanged, so subscribers are not notified.
         */
        setMfaEnabled: (state, action: PayloadAction<boolean>) => {
            if (state.isMfaEnabled === action.payload) {
                return;
            }
            state.isMfaEnabled = action.payload;
        },
        /**
         * Mirror the `mfa-account-verification` flag into the store (called by
         * MfaChallengeHost). No-op when unchanged, so subscribers are not notified.
         */
        setAccVerificationEnabled: (state, action: PayloadAction<boolean>) => {
            if (state.isAccVerificationEnabled === action.payload) {
                return;
            }
            state.isAccVerificationEnabled = action.payload;
        },
        /** Show / hide the post-skip "Setting things up..." view. */
        setFinalizingSkip: (state, action: PayloadAction<boolean>) => {
            state.isFinalizingSkip = action.payload;
        }
    }
});

export const {
    challengeStarted,
    inflightResolved,
    setMfaEnabled,
    setAccVerificationEnabled,
    setFinalizingSkip
} = mfaSlice.actions;

export const selectMfa = (state: { mfa: MfaState }) => state.mfa;
export const selectMfaInflight = (state: { mfa: MfaState }) => state.mfa.inflight;
export const selectIsMfaEnabled = (state: { mfa: MfaState }) => state.mfa.isMfaEnabled;
export const selectIsAccVerificationEnabled = (state: { mfa: MfaState }) =>
    state.mfa.isAccVerificationEnabled;
export const selectIsFinalizingSkip = (state: { mfa: MfaState }) => state.mfa.isFinalizingSkip;

export default mfaSlice.reducer;
